import React, { useEffect, useRef, useCallback, useState } from 'react';
import ThreeDContext from './context';

// Custom hooks
import { useLocation } from 'react-router-dom';
import { useLoadingState } from '../../utils/threeD/useLoadingState';
import useSkuDetails from '../../utils/threeD/useSkuDetails';
import useMobile from '../../utils/threeD/useMobile';
import useQueryParams from '../../utils/threeD/useQueryParams';
import useBackgroundConfig from '../../utils/threeD/useBackgroundConfig';
import usePlayCanvasEngine from '../../utils/threeD/usePlayCanvasEngine';

// Components
import ThreeDLayout from './components/ThreeDLayout';
import ThreeDCanvas from './components/ThreeDCanvas';
import LoadingOverlay from './components/LoadingOverlay';
import AdminControls from './components/AdminControls';

const View3D = ({common=true, hideBottomTabs, setHideBottomTabs}) => {
    const myRef = useRef(null);
    const canvasRef = useRef(null);

    const {isMobile, isMobilePortrait} = useMobile();
    const {thumbnail, skuId, loaded, urls, carName, modelConfig, status, bgId, skuName, enterpriseId, teamId, year, make, model, testingConfig} = useSkuDetails();

    const {
        showLoading,
        progress,
        setShowLoading,
        setProgress,
        reset
    } = useLoadingState();

    let isMobileSmallScreen = true;
    if(myRef && myRef.current){
        isMobileSmallScreen= myRef.current.clientHeight <= 340 && myRef.current.clientWidth <= 600;
    }

    // Get query params
    const useQuery = () => {
        return new URLSearchParams(useLocation().search);
    };

    const query = useQuery();
    const sku_id = query.get('sku_id') || skuId;
    const bg_id = query.get('bg_id') || null;
    const [galleryFile, setGalleryFile] = useState(null);
    const [carFile, setCarFile] = useState(null);
    const [galleryEnabled, setGalleryEnabled] = useState(true);
    const [bgConfig, setBgConfig] = useState(null);

    const queryParams = useQueryParams(common, skuId);
    const { bgConfig: queryBgConfig } = useBackgroundConfig(bgId, queryParams.bg_id);

    const fetchBgConfig = async (bgId) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_BASEURL_API_SPYNE}/3d/api/v1/bg/getById?bg_id=${bgId}`);
            const data = await response.json();
            return data?.data?.config?.config;
        } catch (error) {
            console.error('Error fetching background config:', error);
            return null;
        }
    };

    useEffect(() => {
        const loadBgConfig = async () => {
            if (bg_id || bgId) {
                try {
                    const config = await fetchBgConfig(bg_id || bgId);
                    setBgConfig(config);
                } catch (error) {
                    console.error('Error loading background config:', error);
                }
            }
        };

        loadBgConfig();
    }, [bg_id, bgId]);

    const handleLoadingComplete = useCallback(() => {
        setShowLoading(false);
        if(hideBottomTabs){
            setHideBottomTabs(false);
        }
    }, [hideBottomTabs]);

    const {
        carRef,
        lightsRef,
        galleryEntityRef,
        shadowCasterRef,
        initPlayCanvas,
        cleanup: playCanvasCleanup
    } = usePlayCanvasEngine();

    useEffect(() => {
        const initialize3DScene = async () => {
            if (!canvasRef.current || !(modelConfig || status === "demo")) {
                return;
            }

            const demoBgUrl = `${process.env.REACT_APP_CLOUD_FRONT_URL}/background/${bg_id}/bg.glb`;
            const bgUrl = `${process.env.REACT_APP_CLOUD_FRONT_URL}/background/${bgId}/bg.glb`;
            const finalBgUrl = bg_id ? demoBgUrl : bgUrl;

            try {
                await initPlayCanvas({
                    canvas: canvasRef.current,
                    sku_id: sku_id,
                    bgUrl: finalBgUrl,
                    setProgress,
                    galleryFile,
                    carFile,
                    modelConfig,
                    status,
                    original: queryParams.original,
                    testingConfig,
                    isMobile,
                    isMobilePortrait,
                    adminView: queryParams.adminView,
                    fullControl: queryParams.fullControl,
                    hotspots: queryParams.hotspots,
                    grids: queryParams.grids,
                    galleryEnabled
                });
            } catch (error) {
                console.error('Failed to initialize 3D scene:', error);
            }
        };

        reset();
        initialize3DScene();

        // Cleanup when component unmounts or when sku_id/bgUrl changes
        return () => {
            playCanvasCleanup();
        };
    }, [sku_id, bg_id, bgId, reset, initPlayCanvas, queryParams.inputView, galleryFile, carFile, modelConfig, status, queryParams.original, testingConfig, isMobile, isMobilePortrait, queryParams.adminView, queryParams.fullControl, queryParams.hotspots, queryParams.grids, galleryEnabled, setProgress, canvasRef, playCanvasCleanup]);


    return (
        <ThreeDContext.Provider value={{
            urls, skuId, skuName,carName,  thumbnail, enterpriseId, teamId, year, make, model, modelConfig, status, bgId, loaded
        }}>

        <ThreeDLayout
            inputView={queryParams.inputView}
            isMobilePortrait={isMobilePortrait}
            urls={urls}
            sku_id={queryParams.sku_id}
        >
            <div className="relative w-full h-full touch-none text-left overflow-hidden" ref={myRef}>
                {/* Admin Controls */}
                <AdminControls
                    adminView={queryParams.adminView}
                    carEntity={carRef.current}
                    lights={lightsRef.current}
                    galleryEnabled={galleryEnabled}
                    setGalleryEnabled={setGalleryEnabled}
                    galleryEntity={galleryEntityRef.current}
                    shadowCasterEntity={shadowCasterRef.current}
                    original={queryParams.original}
                    bgConfig={queryBgConfig}
                    hideAdminPanel={queryParams.hideAdminPanel}
                    modelConfig={modelConfig}
                    onGalleryFileChange={(file) => {
                        setGalleryFile(file);
                        reset();
                    }}
                    onCarFileChange={(file) => {
                        setCarFile(file);
                        reset();
                    }}
                />

                {/* Loading Overlay */}
                <LoadingOverlay
                    showLoading={showLoading}
                    progress={progress}
                    onLoadingComplete={handleLoadingComplete}
                />

                {/* 3D Canvas */}
                <ThreeDCanvas
                    ref={canvasRef}
                    showLoading={showLoading}
                    inputView={queryParams.inputView}
                />
            </div>
        </ThreeDLayout>
        </ThreeDContext.Provider>
    );
};

export default View3D;